import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Spinner } from "../../../components/ui/spinner";
import { useTranslation } from "react-i18next";
import AccessLevelField from "./AccessLevelField";
import AdvancedSettings from "./AdvancedSettings";
import FormField from "./FormField";
import { DEFAULT_CONNECTOR_FORM, TRANSPORT_PROTOCOLS } from "../constants";
import { useConnectorForm } from "../hooks/useConnectorForm";
import { useOrganizations } from "../hooks/useOrganizations";
import { useUsers } from "../hooks/useUsers";
import { useMCPProjectTarget } from "../hooks/useMCPProjectTarget";
import type { ConnectorFormValues, TransportProtocol } from "../types";
import { cn } from "@/utils/cn";

export type ConnectorDialogMode = "create" | "edit";

type Props = {
  open: boolean;
  mode?: ConnectorDialogMode;
  initialValues?: ConnectorFormValues;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: ConnectorFormValues) => void | Promise<unknown>;
  isSubmitting?: boolean;
};

const AddConnectorDialog = ({
  open,
  mode = "create",
  initialValues,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const { values, setField, setAccessLevel, setAuthHeader, reset, isValid } =
    useConnectorForm();
  const target = useMCPProjectTarget(open);
  const { seedProject } = target;

  // Refresh the org list on every open (create and edit alike) so the sharing
  // fields resolve against the user's current organizations, not a cached id,
  // and pull the member list with it so recently added teammates are already
  // there when the access level is switched to Users.
  useOrganizations({ enabled: open });
  useUsers({ enabled: open });

  // Hold the latest initialValues without making them an effect dependency.
  // The detail GET resolves after the dialog opens and flips the reference;
  // re-seeding on every change would (a) overwrite the user's edits and
  // (b) form a render → setState → render loop when the dto stays in sync
  // with cache writes from sibling queries.
  const submissionRef = useRef<object | null>(null);
  useEffect(() => {
    if (!open || !isSubmitting) submissionRef.current = null;
  }, [open, isSubmitting]);
  const initialValuesRef = useRef(initialValues);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  useLayoutEffect(() => {
    if (
      open &&
      !wasOpenRef.current &&
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
    ) {
      returnFocusRef.current = document.activeElement;
    }
    wasOpenRef.current = open;
  }, [open]);

  // Re-seed only when the dialog transitions to open — opening Edit for a
  // different connection refreshes the fields, reopening Add starts blank.
  useEffect(() => {
    if (open) {
      reset(initialValuesRef.current ?? DEFAULT_CONNECTOR_FORM);
      seedProject(initialValuesRef.current?.projectId ?? "");
    }
  }, [open, reset, seedProject]);

  const copyKey = `mcpPage.connectorDialog.${mode}`;

  const handleCancel = useCallback(() => {
    if (isSubmitting || submissionRef.current) return;
    onOpenChange(false);
  }, [isSubmitting, onOpenChange]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!isValid || isSubmitting || submissionRef.current) return;
      if (values.accessLevel === "project" && !target.validateProject()) return;
      // The page owns when to close — it waits for the mutation to settle so
      // errors surface before the dialog disappears.
      const operation = {};
      submissionRef.current = operation;
      try {
        const result = onSubmit({ ...values, projectId: target.projectId });
        if (result) void Promise.resolve(result).catch(() => {
          // Mutation hooks own scoped error feedback; keep the form for retry.
        }).finally(() => {
          if (submissionRef.current === operation) submissionRef.current = null;
        });
      } catch (error) {
        submissionRef.current = null;
        throw error;
      }
    },
    [isValid, isSubmitting, onSubmit, values, target],
  );

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if ((isSubmitting || submissionRef.current) && !next) return;
      onOpenChange(next);
    },
    [isSubmitting, onOpenChange],
  );

  const isSubmitDisabled = !isValid || isSubmitting ||
    (values.accessLevel === "project" && !target.isEligible);

  const handleCloseAutoFocus = useCallback(
    (event: Event) => {
      if (!returnFocusRef.current) return;
      event.preventDefault();
      returnFocusRef.current.focus();
      returnFocusRef.current = null;
    },
    [],
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        dir={direction}
        onCloseAutoFocus={handleCloseAutoFocus}
        className={cn(
          "flex max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]",
          values.accessLevel === "organization"
            ? "sm:h-[600px]"
            : "sm:h-[663px]",
        )}
      >
        <DialogHeader className="h-14 shrink-0 justify-center px-4 py-0 pe-12 text-start">
          <DialogTitle className="truncate text-xl leading-6 font-medium">
            {t(`${copyKey}.title`)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(`${copyKey}.description`)}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div
            data-testid="connector-dialog-body"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-2"
          >
            <div className="flex flex-col gap-2">
              <FormField
                label={t("mcpPage.fields.connectorAlias")}
                htmlFor="connector-alias"
                compact
              >
                <Input
                  id="connector-alias"
                  type="text"
                  dir={direction}
                  autoFocus
                  placeholder={t("mcpPage.placeholders.connectorAlias")}
                  value={values.alias}
                  onChange={(e) => setField("alias", e.target.value)}
                  className="rounded-[8px] bg-card"
                />
              </FormField>

              <FormField
                label={t("mcpPage.fields.serverUrl")}
                htmlFor="connector-url"
                compact
              >
                <Input
                  id="connector-url"
                  type="url"
                  dir={direction}
                  placeholder={t("mcpPage.placeholders.serverUrl")}
                  value={values.serverUrl}
                  onChange={(e) => setField("serverUrl", e.target.value)}
                  className="rounded-[8px] bg-card"
                />
              </FormField>

              <FormField
                label={t("mcpPage.fields.transportProtocol")}
                htmlFor="connector-transport"
                compact
              >
                <Select
                  dir={direction}
                  value={values.transport || undefined}
                  onValueChange={(value) =>
                    setField("transport", value as TransportProtocol)
                  }
                >
                  <SelectTrigger
                    id="connector-transport"
                    className="h-9 w-full rounded-[8px] border-border bg-card text-foreground"
                  >
                    <SelectValue placeholder={t("mcpPage.actions.select")} />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSPORT_PROTOCOLS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <AccessLevelField
                accessLevel={values.accessLevel}
                onAccessLevelChange={setAccessLevel}
                projectId={target.projectId}
                onProjectIdChange={(id) => {
                  target.selectProject(id);
                  setField("projectId", id);
                }}
                users={values.users}
                onUsersChange={(users) => setField("users", users)}
                requireCurrentUser
              />

              <AdvancedSettings
                authHeader={values.authHeader}
                onAuthHeaderChange={setAuthHeader}
                oauthClientId={values.oauthClientId}
                onOauthClientIdChange={(value) =>
                  setField("oauthClientId", value)
                }
                oauthClientSecret={values.oauthClientSecret}
                onOauthClientSecretChange={(value) =>
                  setField("oauthClientSecret", value)
                }
                apiKey={values.apiKey}
                onApiKeyChange={(value) => setField("apiKey", value)}
                bearerToken={values.bearerToken}
                onBearerTokenChange={(value) =>
                  setField("bearerToken", value)
                }
              />

              <p className="border-t border-border pt-3 text-sm text-muted-foreground">
                {t("mcpPage.connectorDialog.trustWarning")}
              </p>
            </div>
          </div>

          <DialogFooter className="h-[68px] shrink-0 flex-row items-center justify-end gap-2 px-4 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t("mcpPage.actions.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              aria-busy={isSubmitting}
            >
              {isSubmitting && <Spinner className="size-4" />}
              {isSubmitting
                ? t(`${copyKey}.submitBusy`)
                : t(`${copyKey}.submitIdle`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddConnectorDialog;
